import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where
} from 'firebase/firestore';

import { db } from '../utils/firebase';
import {
  getJobBankRoleFromUserData,
  isAdmin as isJobBankAdminRole,
  JOB_BANK_ROLES,
  roleMatches
} from '../components/jobBankRole';

const CAREER_COACHES_COLLECTION = 'careerCoaches';
const LEGACY_PROFILES_COLLECTION = 'careerCoachProfiles';
const USERS_COLLECTION = 'users';

const SHARED_IDENTITY_FIELDS = [
  'firstName',
  'lastName',
  'displayName',
  'city',
  'province',
  'photoURL'
];

const CAREER_COACH_ROLE_QUERIES = [
  ['jobBankRole', JOB_BANK_ROLES.CAREER_COACH],
  ['JobBankRole', JOB_BANK_ROLES.CAREER_COACH],
  ['jobbankRole', JOB_BANK_ROLES.CAREER_COACH]
];

function clean(value) {
  return String(value || '').trim();
}

export function isPlatformAdmin(userData = {}) {
  return userData?.role === 'admin' || userData?.isAdmin === true;
}

export function canBootstrapCoachDirectory(userData = {}, role = '') {
  return isPlatformAdmin(userData) || isJobBankAdminRole(role);
}

export function isCareerCoachUser(userData = {}) {
  return roleMatches(getJobBankRoleFromUserData(userData), [
    JOB_BANK_ROLES.CAREER_COACH
  ]);
}

export function getCoachDisplayName(profile = {}) {
  return (
    [profile.firstName, profile.lastName].filter(Boolean).join(' ') ||
    profile.displayName ||
    profile.email ||
    'Career Coach'
  );
}

export function buildDirectoryCoachDoc(uid, userData = {}, legacyProfile = {}, existingCoach = {}) {
  const specialties =
    legacyProfile.specialties ||
    existingCoach.specialties ||
    userData.specialties ||
    userData.primaryOccupation ||
    '';

  const listedInDirectory =
    existingCoach.listedInDirectory !== undefined
      ? existingCoach.listedInDirectory === true
      : legacyProfile.listedInDirectory !== undefined
        ? legacyProfile.listedInDirectory === true
        : true;

  return {
    uid,
    firstName: existingCoach.firstName || userData.firstName || legacyProfile.firstName || '',
    lastName: existingCoach.lastName || userData.lastName || legacyProfile.lastName || '',
    displayName: existingCoach.displayName || userData.displayName || legacyProfile.displayName || '',
    email: existingCoach.email || userData.email || legacyProfile.email || '',
    phone: existingCoach.phone || userData.phone || legacyProfile.phone || '',
    city: existingCoach.city || userData.city || legacyProfile.city || '',
    province: existingCoach.province || userData.province || legacyProfile.province || '',
    photoURL: existingCoach.photoURL || userData.photoURL || legacyProfile.photoURL || '',
    coachBio:
      existingCoach.coachBio ||
      legacyProfile.coachBio ||
      legacyProfile.bio ||
      userData.coachBio ||
      userData.bio ||
      '',
    specialties: clean(specialties),
    listedInDirectory,
    showEmail: existingCoach.showEmail === true,
    showPhone: existingCoach.showPhone === true,
    jobBankRole: JOB_BANK_ROLES.CAREER_COACH,
    updatedAt: serverTimestamp()
  };
}

async function fetchCareerCoachUserDocs() {
  const snapshots = await Promise.all(
    CAREER_COACH_ROLE_QUERIES.map(([field, value]) =>
      getDocs(
        query(
          collection(db, USERS_COLLECTION),
          where(field, '==', value)
        )
      ).catch(() => ({ docs: [] }))
    )
  );

  const usersById = new Map();

  snapshots.forEach((snapshot) => {
    snapshot.docs.forEach((userDoc) => {
      usersById.set(userDoc.id, userDoc);
    });
  });

  return Array.from(usersById.values());
}

async function readLegacyProfile(uid) {
  const legacySnap = await getDoc(doc(db, LEGACY_PROFILES_COLLECTION, uid)).catch(() => null);
  return legacySnap?.exists?.() ? legacySnap.data() : {};
}

async function readUserData(uid) {
  const userSnap = await getDoc(doc(db, USERS_COLLECTION, uid)).catch(() => null);
  return userSnap?.exists?.() ? userSnap.data() : null;
}

export async function syncCoachDirectoryFromUser(uid) {
  if (!uid) return null;

  const userData = await readUserData(uid);

  if (!userData || !isCareerCoachUser(userData)) {
    return null;
  }

  const coachRef = doc(db, CAREER_COACHES_COLLECTION, uid);
  const coachSnap = await getDoc(coachRef);
  const existingCoach = coachSnap.exists() ? coachSnap.data() : {};
  const legacyProfile = await readLegacyProfile(uid);

  const payload = {
    ...buildDirectoryCoachDoc(uid, userData, legacyProfile, existingCoach),
    createdAt: existingCoach.createdAt || serverTimestamp()
  };

  await setDoc(coachRef, payload, { merge: true });

  return {
    id: uid,
    ...payload
  };
}

/**
 * Ensures careerCoaches/{uid} exists by seeding from users + legacy careerCoachProfiles.
 */
export async function ensureCoachDirectoryProfile(uid, options = {}) {
  const {
    syncFromUser = false,
    skipIfExists = false,
    allowLegacyOnly = false
  } = options;

  if (!uid) return null;

  const coachRef = doc(db, CAREER_COACHES_COLLECTION, uid);
  const coachSnap = await getDoc(coachRef);

  if (coachSnap.exists()) {
    if (syncFromUser) {
      return syncCoachDirectoryFromUser(uid);
    }

    if (skipIfExists) {
      return {
        id: coachSnap.id,
        ...coachSnap.data()
      };
    }

    return {
      id: coachSnap.id,
      ...coachSnap.data()
    };
  }

  const userData = await readUserData(uid);
  const legacyProfile = await readLegacyProfile(uid);

  if (userData) {
    if (!isCareerCoachUser(userData) && !(allowLegacyOnly && Object.keys(legacyProfile).length > 0)) {
      return null;
    }
  } else if (!(allowLegacyOnly && Object.keys(legacyProfile).length > 0)) {
    return null;
  }

  const payload = {
    ...buildDirectoryCoachDoc(uid, userData || {}, legacyProfile),
    createdAt: serverTimestamp()
  };

  await setDoc(coachRef, payload, { merge: true });

  return {
    id: uid,
    ...payload
  };
}

async function bootstrapLegacyCoachProfiles({
  writerUid,
  canWriteOthers = false
} = {}) {
  const migrated = [];
  const legacySnap = await getDocs(
    collection(db, LEGACY_PROFILES_COLLECTION)
  ).catch(() => ({ docs: [] }));

  for (const legacyDoc of legacySnap.docs) {
    const coachId = legacyDoc.id;

    if (!canWriteOthers && writerUid !== coachId) {
      continue;
    }

    const coach = await ensureCoachDirectoryProfile(coachId, {
      allowLegacyOnly: true,
      skipIfExists: true
    });

    if (coach) {
      migrated.push(coachId);
    }
  }

  return migrated;
}

/**
 * Admin/bootstrap helper: migrate legacy careerCoachProfiles and seed missing
 * careerCoaches docs for users with the Career Coach role.
 */
export async function bootstrapCoachDirectoryProfiles({ isAdmin = false } = {}) {
  if (!isAdmin) {
    return bootstrapLegacyCoachProfiles({ canWriteOthers: false });
  }

  const migrated = [];

  const legacyMigrated = await bootstrapLegacyCoachProfiles({
    canWriteOthers: true
  });
  migrated.push(...legacyMigrated);

  const coachUserDocs = await fetchCareerCoachUserDocs();

  for (const userDoc of coachUserDocs) {
    const userData = userDoc.data();

    if (!isCareerCoachUser(userData)) {
      continue;
    }

    const coach = await ensureCoachDirectoryProfile(userDoc.id, {
      syncFromUser: true
    });

    if (coach) {
      migrated.push(userDoc.id);
    }
  }

  return [...new Set(migrated)];
}

/**
 * Runs the appropriate sync path before listing coaches in the directory.
 */
export async function syncCoachDirectoryBeforeListing({
  uid,
  userData = {},
  role = ''
} = {}) {
  const canBootstrapAll = canBootstrapCoachDirectory(userData, role);
  const isCoach = roleMatches(role, [JOB_BANK_ROLES.CAREER_COACH]);

  await bootstrapLegacyCoachProfiles({
    writerUid: uid,
    canWriteOthers: canBootstrapAll
  });

  if (isCoach && uid) {
    await ensureCoachDirectoryProfile(uid, { syncFromUser: true });
  }

  if (canBootstrapAll) {
    await bootstrapCoachDirectoryProfiles({ isAdmin: true });
  }
}

export async function listDirectoryCoaches(syncOptions = null) {
  try {
    if (syncOptions) {
      await syncCoachDirectoryBeforeListing(syncOptions);
    }

    const snapshot = await getDocs(
      collection(db, CAREER_COACHES_COLLECTION)
    );

    return snapshot.docs
      .map(coachDoc => ({
        id: coachDoc.id,
        ...coachDoc.data()
      }))
      .filter(coach => coach.listedInDirectory !== false)
      .sort((a, b) =>
        getCoachDisplayName(a).localeCompare(getCoachDisplayName(b))
      );
  } catch (error) {
    console.error('listDirectoryCoaches error:', error);
    return [];
  }
}

export async function getDirectoryCoach(uid, syncOptions = null) {
  if (!uid) return null;

  try {
    if (syncOptions) {
      await syncCoachDirectoryBeforeListing(syncOptions);

      if (syncOptions.uid === uid) {
        await ensureCoachDirectoryProfile(uid, { syncFromUser: true });
      }
    }

    const coachSnap = await getDoc(doc(db, CAREER_COACHES_COLLECTION, uid));

    if (!coachSnap.exists()) {
      return null;
    }

    const coach = {
      id: coachSnap.id,
      ...coachSnap.data()
    };

    if (coach.listedInDirectory === false) {
      return null;
    }

    return coach;
  } catch (error) {
    console.error('getDirectoryCoach error:', error);
    return null;
  }
}

export async function loadCoachPublicProfileForEdit(uid) {
  if (!uid) return null;

  const [userData, coachSnap, legacyProfile] = await Promise.all([
    readUserData(uid),
    getDoc(doc(db, CAREER_COACHES_COLLECTION, uid)),
    readLegacyProfile(uid)
  ]);

  const existingCoach = coachSnap.exists() ? coachSnap.data() : {};

  return buildDirectoryCoachDoc(uid, userData || {}, legacyProfile, existingCoach);
}

export async function saveCoachPublicProfile(uid, profile = {}) {
  if (!uid) {
    throw new Error('Coach uid is required.');
  }

  const coachRef = doc(db, CAREER_COACHES_COLLECTION, uid);
  const userRef = doc(db, USERS_COLLECTION, uid);

  const directoryPayload = {
    uid,
    firstName: clean(profile.firstName),
    lastName: clean(profile.lastName),
    displayName: clean(profile.displayName),
    email: clean(profile.email),
    phone: clean(profile.phone),
    city: clean(profile.city),
    province: clean(profile.province),
    photoURL: clean(profile.photoURL),
    coachBio: clean(profile.coachBio),
    specialties: clean(profile.specialties),
    listedInDirectory: profile.listedInDirectory !== false,
    showEmail: profile.showEmail === true,
    showPhone: profile.showPhone === true,
    jobBankRole: JOB_BANK_ROLES.CAREER_COACH,
    updatedAt: serverTimestamp()
  };

  const userPayload = {};

  SHARED_IDENTITY_FIELDS.forEach((field) => {
    if (profile[field] !== undefined) {
      userPayload[field] = clean(profile[field]);
    }
  });

  if (profile.email !== undefined) {
    userPayload.email = clean(profile.email);
  }

  if (profile.phone !== undefined) {
    userPayload.phone = clean(profile.phone);
  }

  if (profile.coachBio !== undefined) {
    userPayload.coachBio = clean(profile.coachBio);
  }

  if (profile.specialties !== undefined) {
    userPayload.specialties = clean(profile.specialties);
  }

  userPayload.updatedAt = serverTimestamp();

  const existingCoach = await getDoc(coachRef);

  await setDoc(
    coachRef,
    {
      ...directoryPayload,
      createdAt: existingCoach.exists()
        ? existingCoach.data().createdAt
        : serverTimestamp()
    },
    { merge: true }
  );

  if (Object.keys(userPayload).length > 0) {
    await updateDoc(userRef, userPayload);
  }

  return { success: true };
}

export function getPublicContactFields(coach = {}) {
  return {
    email: coach.showEmail ? coach.email : '',
    phone: coach.showPhone ? coach.phone : ''
  };
}

/**
 * Returns an audit summary for admins comparing users with Career Coach role
 * against careerCoaches directory documents.
 */
export async function auditCoachDirectoryCoverage() {
  const [coachUserDocs, directorySnap] = await Promise.all([
    fetchCareerCoachUserDocs(),
    getDocs(collection(db, CAREER_COACHES_COLLECTION))
  ]);

  const directoryIds = new Set(directorySnap.docs.map(docSnap => docSnap.id));
  const eligibleCoachIds = coachUserDocs
    .filter(userDoc => isCareerCoachUser(userDoc.data()))
    .map(userDoc => userDoc.id);

  const missingDirectoryDocs = eligibleCoachIds.filter(uid => !directoryIds.has(uid));
  const hiddenDirectoryDocs = directorySnap.docs
    .filter(docSnap => docSnap.data().listedInDirectory === false)
    .map(docSnap => docSnap.id);

  return {
    eligibleCoachCount: eligibleCoachIds.length,
    directoryDocCount: directorySnap.docs.length,
    listedCoachCount: directorySnap.docs.filter(docSnap => docSnap.data().listedInDirectory !== false).length,
    missingDirectoryDocs,
    hiddenDirectoryDocs
  };
}
